import { z } from 'zod'
import {
  BirthdateValues,
  InvoiceBirthdateSchema,
  InvoiceSchema,
  InvoiceSchemaVat,
} from '@ca/common'
import type { RegionPolicyConfig } from '@ca/common/types'
import { contactModalSchema } from '@user/customer/contactSchemas'
import { AddressVatSchema } from 'utils/vatSchema'
import { personalInfoStepSchema } from './schemas'
import type { CreateCustomerData } from './schemas'
import { CreateStepKey } from './types'

const personalBirthdateSchema = z.coerce
  .date({
    error: (issue) => {
      if (issue.code === 'invalid_type') {
        return 'required'
      }
    },
  })
  .min(BirthdateValues.min, 'birthday.min')
  .max(new Date(), 'birthday.max')

export const personalStepSchema = personalInfoStepSchema.extend({
  birthday: personalBirthdateSchema,
})

export const contactStepSchema = contactModalSchema

export const addressStepSchema = AddressVatSchema

const invoiceBirthDateRequired = (
  data: { birth_date: string | null },
  ctx: z.RefinementCtx
) => {
  if (!data.birth_date) {
    ctx.addIssue({ code: 'custom', message: 'required', path: ['birth_date'] })
  }
}

export const getInvoiceStepSchema = (requiresVat: boolean) => {
  const base = requiresVat ? InvoiceSchemaVat : InvoiceSchema
  return base.superRefine(invoiceBirthDateRequired)
}

export const getInvoiceFormSchema = (requiresVat: boolean) => {
  const base = requiresVat ? InvoiceSchemaVat : InvoiceSchema
  return base
    .omit({ birth_date: true })
    .extend({ birth_date: InvoiceBirthdateSchema.nullable() })
    .superRefine((data, ctx) => {
      if (!data.birth_date) {
        ctx.addIssue({ code: 'custom', message: 'required', path: ['birth_date'] })
      }
    })
}

export const getPoliciesStepSchema = (regionPolicies: RegionPolicyConfig[]) =>
  z.record(z.string(), z.boolean()).superRefine((policies, ctx) => {
    for (const policy of regionPolicies.filter((p) => p.required)) {
      if (policies[policy.policy_key] !== true) {
        ctx.addIssue({
          code: 'custom',
          message: 'required',
          path: [policy.policy_key],
        })
      }
    }
  })

export type StepValidationContext = {
  regionPolicies: RegionPolicyConfig[]
  invoiceRequiresVat: boolean
}

export const pickStepSlice = (
  step: CreateStepKey,
  data: Partial<CreateCustomerData>
) => {
  switch (step) {
    case 'personal':
      return {
        first_name: data.first_name,
        last_name: data.last_name,
        birthday: data.birthday,
      }
    case 'contact':
      return {
        email: data.email,
        mobile: data.mobile,
        phone: data.phone,
      }
    case 'address':
      return data.address
    case 'invoice':
      return data.invoice
    case 'policies':
      return data.policies ?? {}
  }
}

export const getStepSchema = (
  step: CreateStepKey,
  ctx: StepValidationContext
): z.ZodType => {
  switch (step) {
    case 'personal':
      return personalStepSchema
    case 'contact':
      return contactStepSchema
    case 'address':
      return addressStepSchema
    case 'invoice':
      return getInvoiceStepSchema(ctx.invoiceRequiresVat)
    case 'policies':
      return getPoliciesStepSchema(ctx.regionPolicies)
  }
}
